// Bandeja de lo que llega del portal del cliente.
//
// Dos colas distintas que se parecen pero no son lo mismo:
//   · Altas nuevas: viviendas que un propietario ha subido y que aún no existen
//     para nadie. Aprobarlas les da código HAB-XXX y, si se quiere, las publica.
//   · Cambios propuestos: la vivienda YA está publicada y el propietario pide
//     tocar algo. Lo publicado no se mueve hasta que se acepte.
//
// Va arriba del todo de Viviendas y solo aparece si hay algo que resolver: una
// bandeja vacía permanente se convierte en ruido y se deja de mirar.
import { useState } from 'react';
import { Check, Inbox, PencilLine, X } from 'lucide-react';
import { useAprobarAlta, useRechazarAlta, useResolverCambios } from '../api/properties';
import { TIPO_LABEL, type Property } from '../lib/types';

const eur = (n?: number) => (n ? `${n.toLocaleString('es-ES')} €` : '—');

export default function BandejaPortal({ items }: { items: Property[] }) {
  const pendientes = items.filter((p) => p.estado === 'pendiente_validacion');
  const conCambios = items.filter((p) => p.cambios_pendientes);

  if (!pendientes.length && !conCambios.length) return null;

  return (
    <section className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-4 sm:p-5">
      <header className="flex items-center gap-2">
        <Inbox size={18} className="text-amber-700" />
        <h2 className="font-extrabold text-amber-900">Llegado del portal de clientes</h2>
        <span className="rounded-full bg-amber-200 px-2.5 py-0.5 text-[0.7rem] font-bold text-amber-900">
          {pendientes.length + conCambios.length}
        </span>
      </header>

      <div className="mt-4 space-y-3">
        {pendientes.map((p) => (
          <AltaPendiente key={p.property_id} p={p} />
        ))}
        {conCambios.map((p) => (
          <CambioPropuesto key={`c-${p.property_id}`} p={p} />
        ))}
      </div>
    </section>
  );
}

function AltaPendiente({ p }: { p: Property }) {
  const aprobar = useAprobarAlta();
  const rechazar = useRechazarAlta();
  const [motivo, setMotivo] = useState('');
  const [rechazando, setRechazando] = useState(false);

  const ocupado = aprobar.isPending || rechazar.isPending;

  return (
    <article className="rounded-xl border border-amber-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-deep">{p.direccion}</p>
          <p className="text-sm text-ink/55">
            {p.zona || 'Madrid'} · {TIPO_LABEL[p.tipo] ?? p.tipo} · {p.m2 ? `${p.m2} m²` : 'sin m²'} ·{' '}
            {p.habitaciones ? `${p.habitaciones} hab` : 'sin habitaciones'}
          </p>
          <p className="mt-1 text-sm font-semibold text-brand">
            {p.operacion === 'venta'
              ? eur(p.precio_venta)
              : `${eur(p.precio_alquiler)}/mes`}
          </p>
          {p.descripcion && (
            <p className="mt-2 max-w-prose whitespace-pre-line text-sm text-ink/70">
              {p.descripcion}
            </p>
          )}
        </div>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-[0.68rem] font-bold uppercase tracking-wider text-amber-900">
          Alta nueva
        </span>
      </div>

      {!rechazando ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            disabled={ocupado}
            onClick={() => aprobar.mutate({ propertyId: p.property_id, publicar: true })}
            className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
          >
            <Check size={15} /> Aceptar y publicar
          </button>
          <button
            disabled={ocupado}
            onClick={() => aprobar.mutate({ propertyId: p.property_id, publicar: false })}
            className="rounded-full border border-deep/15 px-4 py-2 text-sm font-bold text-deep transition hover:border-brand disabled:opacity-50"
          >
            Aceptar sin publicar
          </button>
          <button
            disabled={ocupado}
            onClick={() => setRechazando(true)}
            className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
          >
            Devolver al propietario
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {/* El motivo es obligatorio también en el servidor: un rechazo mudo
              deja al propietario sin saber qué corregir y acaba en llamada. */}
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            placeholder="Qué tiene que corregir. Lo va a leer él tal cual."
            className="w-full rounded-xl border border-deep/15 px-3 py-2 text-sm outline-none focus:border-brand"
          />
          <div className="flex gap-2">
            <button
              disabled={!motivo.trim() || ocupado}
              onClick={() => rechazar.mutate({ propertyId: p.property_id, motivo })}
              className="flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-red-700 disabled:opacity-50"
            >
              <X size={15} /> Devolver
            </button>
            <button
              onClick={() => setRechazando(false)}
              className="rounded-full border border-deep/15 px-4 py-2 text-sm font-bold text-deep"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {(aprobar.isError || rechazar.isError) && (
        <p className="mt-2 text-sm font-semibold text-red-600">
          {((aprobar.error ?? rechazar.error) as Error)?.message}
        </p>
      )}
    </article>
  );
}

function CambioPropuesto({ p }: { p: Property }) {
  const resolver = useResolverCambios();
  const propuesta = p.cambios_pendientes ?? {};

  // Solo lo que cambia de verdad: enseñar los veinte campos con dieciocho
  // iguales obliga a buscar la diferencia a ojo.
  const actual = p as unknown as Record<string, unknown>;
  const diferencias = Object.entries(propuesta).filter(
    ([campo, valor]) => campo !== 'propuesto_en' && valor !== actual[campo],
  );

  return (
    <article className="rounded-xl border border-sky-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-deep">
            {p.direccion} {p.codigo && <span className="text-ink/40">· {p.codigo}</span>}
          </p>
          <p className="text-sm text-ink/55">
            El propietario propone {diferencias.length}{' '}
            {diferencias.length === 1 ? 'cambio' : 'cambios'}. Lo publicado sigue
            como estaba.
          </p>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1 text-[0.68rem] font-bold uppercase tracking-wider text-sky-900">
          <PencilLine size={12} /> Cambio
        </span>
      </div>

      {diferencias.length > 0 && (
        <dl className="mt-3 space-y-1.5 rounded-xl bg-soft px-3 py-2.5 text-sm">
          {diferencias.map(([campo, nuevo]) => (
            <div key={campo} className="flex flex-wrap items-baseline gap-x-2">
              <dt className="font-bold text-ink/50">{campo.replace(/_/g, ' ')}:</dt>
              <dd className="text-ink/45 line-through">
                {String(actual[campo] ?? '—')}
              </dd>
              <dd className="font-bold text-deep">→ {String(nuevo ?? '—')}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          disabled={resolver.isPending}
          onClick={() => resolver.mutate({ propertyId: p.property_id, aceptar: true })}
          className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-50"
        >
          <Check size={15} /> Aplicar los cambios
        </button>
        <button
          disabled={resolver.isPending}
          onClick={() => resolver.mutate({ propertyId: p.property_id, aceptar: false })}
          className="rounded-full border border-deep/15 px-4 py-2 text-sm font-bold text-deep transition hover:border-brand disabled:opacity-50"
        >
          Descartar
        </button>
      </div>
    </article>
  );
}
